import { defineStore } from 'pinia';
import { ref, computed, toRefs } from 'vue';
import { useMapStore } from '@/features/map/store/map-store';

export const usePanelStore = defineStore('panel', () => {
  // State
  const openPanels = ref([]);
  const PANEL_WIDTH = ref(360);
  const PANEL_GAP = ref(20);
  const DEFAULT_LEFT_GAP = ref(20);
  const TRANSITION = ref('left 0.3s ease');

  // Getters
  const { showMainMenu, showProjectMenu } = toRefs(useMapStore());

  const baseOffset = computed(() => {
    return showMainMenu.value || showProjectMenu.value
      ? PANEL_WIDTH.value + PANEL_GAP.value
      : 0;
  });

  const panelPositions = computed(() =>
    openPanels.value.reduce((acc, panelId, index) => {
      acc[panelId] = {
        left: `${DEFAULT_LEFT_GAP.value + baseOffset.value + index * (PANEL_WIDTH.value + PANEL_GAP.value)}px`,
        transition: TRANSITION.value,
      };
      return acc;
    }, {}),
  );

  const isPanelOpen = (panelId) => openPanels.value.includes(panelId);

  // Actions
  function addPanel(panelId) {
    if (!openPanels.value.includes(panelId)) {
      openPanels.value.push(panelId);
    }
  }

  function removePanel(panelId) {
    openPanels.value = openPanels.value.filter((id) => id !== panelId);
  }

  function togglePanel(panelId) {
    if (isPanelOpen(panelId)) {
      removePanel(panelId);
    } else {
      addPanel(panelId);
    }
  }

  // Optional actions
  function clearPanels() {
    openPanels.value = [];
  }

  function movePanelToFront(panelId) {
    if (isPanelOpen(panelId)) {
      openPanels.value = [
        ...openPanels.value.filter((id) => id !== panelId),
        panelId,
      ];
    }
  }

  function updatePanelDimensions({ width, gap, leftGap, transition }) {
    if (width) PANEL_WIDTH.value = width;
    if (gap) PANEL_GAP.value = gap;
    if (leftGap) DEFAULT_LEFT_GAP.value = leftGap;
    if (transition) TRANSITION.value = transition;
  }

  return {
    panelPositions,
    addPanel,
    removePanel,
    togglePanel,
  };
});
